import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "@mantine/form";
import { useStaffFormSalon } from "./useStaffFormSalon";
import { convertTo24HourFormat } from "../../../utils/timeUtils";
import type {
  DayHours,
  DayKey,
  StaffFormInitialData,
  StaffFormIssue,
  StaffFormValues,
  StaffRole,
  StaffServiceAssignment,
} from "../../../types/staffForm";
import {
  buildInitialValues,
  defaultBookableForRole,
  validateStaffForm,
} from "./staffFormUtils";
import { buildAssignmentsForStaff } from "./staffServiceUtils";

/**
 * Form state for the staff form: values + field errors via @mantine/form, plus
 * the session trackers the save rules depend on.
 *
 * The trackers are plain refs rather than Mantine's touched state on purpose:
 * their meaning is domain-specific ("touched AND holds a value" decides
 * whether a day is sent; "toggled at least once, even if switched back"
 * decides whether `has_bookable_calendar` is sent) and they must reset exactly
 * when the record reloads.
 */
export function useStaffForm(initialData?: StaffFormInitialData | null) {
  const { businessInfo, services } = useStaffFormSalon();
  const staffId = initialData?.id ?? null;
  const isCreate = !staffId;

  /** Day keys whose hour inputs the user edited in this form session. */
  const touchedHourDays = useRef<Set<DayKey>>(new Set());
  /** On create, the bookable switch tracks the role until the user touches it. */
  const userToggledBookable = useRef(false);
  /** Once the Menu is edited, late-arriving services no longer re-seed it. */
  const menuTouched = useRef(false);

  // The staff member's current assignments as stored server-side.
  const baselineAssignments = useMemo(
    () => buildAssignmentsForStaff(services, staffId),
    [services, staffId],
  );

  const form = useForm<StaffFormValues>({
    mode: "controlled",
    initialValues: buildInitialValues({
      initialData,
      businessInfo,
      serviceAssignments: baselineAssignments,
    }),
  });

  // Re-seed whenever the record or the salon changes.
  useEffect(() => {
    touchedHourDays.current = new Set();
    userToggledBookable.current = false;
    menuTouched.current = false;
    form.setValues(
      buildInitialValues({
        initialData,
        businessInfo,
        serviceAssignments: baselineAssignments,
      }),
    );
    form.clearErrors();
  }, [initialData, businessInfo]);

  // Services can load after the form opens; adopt them until the Menu is edited.
  useEffect(() => {
    if (!menuTouched.current) {
      form.setFieldValue("serviceAssignments", baselineAssignments);
    }
  }, [baselineAssignments]);

  const setRole = useCallback(
    (role: StaffRole) => {
      form.setFieldValue("role", role);
      if (isCreate && !userToggledBookable.current) {
        form.setFieldValue("hasBookableCalendar", defaultBookableForRole(role));
      }
    },
    [form, isCreate],
  );

  const setBookable = useCallback(
    (checked: boolean) => {
      userToggledBookable.current = true;
      form.setFieldValue("hasBookableCalendar", checked);
    },
    [form],
  );

  const setDayHours = useCallback(
    (day: DayKey, next: DayHours) => {
      touchedHourDays.current.add(day);
      form.setFieldValue("hours", { ...form.getValues().hours, [day]: next });
    },
    [form],
  );

  /**
   * Checking a day seeds it from the salon's hours for that day ("" when the
   * salon is closed, so the row stays checked with blank inputs to fill);
   * unchecking clears it to the "not working" nulls.
   */
  const toggleDay = useCallback(
    (day: DayKey, checked: boolean) => {
      setDayHours(
        day,
        checked
          ? {
              start:
                convertTo24HourFormat(businessInfo?.[`${day}_open_time`]) ?? "",
              end:
                convertTo24HourFormat(businessInfo?.[`${day}_end_time`]) ?? "",
            }
          : { start: null, end: null },
      );
    },
    [businessInfo, setDayHours],
  );

  const setDayTime = useCallback(
    (day: DayKey, side: keyof DayHours, value: string) => {
      setDayHours(day, { ...form.getValues().hours[day], [side]: value });
    },
    [form, setDayHours],
  );

  const setAssignment = useCallback(
    (serviceId: string, next: StaffServiceAssignment) => {
      menuTouched.current = true;
      form.setFieldValue("serviceAssignments", {
        ...form.getValues().serviceAssignments,
        [serviceId]: next,
      });
    },
    [form],
  );

  /** Runs every rule, sets the field-level errors, and returns all issues in order. */
  const validate = useCallback((): StaffFormIssue[] => {
    const issues = validateStaffForm(form.getValues(), {
      initialData,
      touchedDays: touchedHourDays.current,
    });
    form.setErrors(
      Object.fromEntries(
        issues
          .filter((issue) => issue.field)
          .map((issue) => [issue.field as string, issue.message]),
      ),
    );
    return issues;
  }, [form, initialData]);

  return {
    form,
    isCreate,
    staffId,
    baselineAssignments,
    touchedHourDays,
    userToggledBookable,
    setRole,
    setBookable,
    toggleDay,
    setDayTime,
    setAssignment,
    validate,
  };
}

export type StaffFormState = ReturnType<typeof useStaffForm>;
